import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Waveform, Copy, CheckCircle, Phone, WarningCircle } from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { RegisterNumberDialog, UpdateSecretDialog, VoiceKnowledge } from "@/pages/Voice";

export function VoiceSetup() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ["voice-status"], queryFn: async () => (await api.get("/voice/status")).data });
  const [copied, setCopied] = useState(false);

  const copyWebhook = () => {
    navigator.clipboard.writeText(status?.webhook_url || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="space-y-4" data-testid="voice-setup">
      {/* Grok connection status */}
      <div className="rounded-lg border border-border bg-card p-4" data-testid="voice-status-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center">
              <Waveform size={20} weight="fill" />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-heading font-semibold text-sm">Grok Voice Agent</span>
                {status?.configured ? (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-success/10 text-success border border-success/20 font-medium flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-success inline-block" /> Live
                  </span>
                ) : status ? (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-destructive/10 text-destructive border border-destructive/20 font-medium">Not configured</span>
                ) : (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border font-medium">Loading…</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Voice <span className="font-medium text-foreground capitalize">{status?.voice || "eve"}</span> · model{" "}
                <span className="font-medium text-foreground">{status?.model || "grok-voice-latest"}</span>
              </div>
            </div>
          </div>
          {user?.is_admin && (
            <div className="flex items-center gap-1.5">
              <UpdateSecretDialog numbers={status?.numbers || []} onDone={() => qc.invalidateQueries({ queryKey: ["voice-status"] })} />
              <RegisterNumberDialog onDone={() => qc.invalidateQueries({ queryKey: ["voice-status"] })} />
            </div>
          )}
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">Inbound webhook URL</Label>
            <div className="mt-1 flex items-center gap-2">
              <code data-testid="voice-webhook-url" className="flex-1 text-[12px] bg-secondary border border-border rounded-md px-2.5 py-1.5 truncate">{status?.webhook_url}</code>
              <Button size="sm" variant="outline" onClick={copyWebhook} data-testid="copy-webhook-btn" className="shrink-0">
                {copied ? <CheckCircle size={15} className="text-success" /> : <Copy size={15} />}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Point your xAI number's <code>realtime.call.incoming</code> webhook here.</p>
          </div>
          <div>
            <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">Registered numbers</Label>
            <div className="mt-1 space-y-1.5" data-testid="voice-numbers-list">
              {status?.numbers?.length ? status.numbers.map((n) => (
                <div key={n.phone_number} className="flex items-center gap-2 text-sm">
                  <Phone size={14} className="text-primary" weight="fill" />
                  <span className="font-medium">{n.phone_number}</span>
                  {!n.has_secret && <span className="text-[10px] text-amber-600 flex items-center gap-0.5"><WarningCircle size={12} /> no signing secret</span>}
                </div>
              )) : <p className="text-sm text-muted-foreground">No numbers registered here yet — configure the webhook above on your xAI number, or register one.</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Knowledge base (admin only) */}
      {user?.is_admin && <VoiceKnowledge />}
    </div>
  );
}
